import React, { useState, useEffect, useCallback } from 'react';
import './RetrospectiveEntry.css'; 
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotification } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

// === API CONFIGURATION ===
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return ''; 
};

const API_URL = getApiUrl();

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper for currency
const renderCurrency = (val) => val ? parseFloat(val).toLocaleString('en-US', {style: 'currency', currency: 'USD'}) : '$0.00';

function RetrospectiveEntry() {
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // --- TAB STATE ---
  const [activeTab, setActiveTab] = useState('missing');

  // --- SHARED STATE ---
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- STATE TAB 1: MISSING BATCH ---
  const [selectedHotelId, setSelectedHotelId] = useState('');
  const [startDate, setStartDate] = useState(new Date().getFullYear() + '-01-01');
  const [missingBatch, setMissingBatch] = useState([]);
  const [totalMissing, setTotalMissing] = useState(0);
  const [inputData, setInputData] = useState({});
  const [saving, setSaving] = useState(false);

  // --- STATE TAB 2: SEARCH ---
  const [searchParams, setSearchParams] = useState({
      startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
      hotelId: ''
  });
  const [searchResults, setSearchResults] = useState([]);

  // --- STATE TAB 3 & 4: ANOMALIES ---
  const [anomalies, setAnomalies] = useState([]);
  const [maxCapAnomalies, setMaxCapAnomalies] = useState([]);

  // Edit State (Shared for anomalies)
  const [editingKey, setEditingKey] = useState(null); 
  const [editValues, setEditValues] = useState({ occupied_rooms: '', average_rate: '' });

  // Load Hotels
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await fetch(`${API_URL}/api.php?path=hotels&limit=999`, { headers: getAuthHeader() });
        const data = await response.json();
        if (data.success) {
          setHotels(data.data.hotels || []);
        } else {
          showNotification('Error al cargar hoteles', 'error');
        }
      } catch (err) {
        console.error(err);
        showNotification('Error de conexión', 'error');
      }
    };
    fetchHotels();
  }, [showNotification]);

  // --- TAB 1: MISSING BATCH LOGIC ---
  const fetchMissingBatch = useCallback(async (hotelId, start) => {
      if (!hotelId) return;
      setLoading(true);
      setMissingBatch([]);
      try {
        const url = `${API_URL}/api.php?path=missing-batch&hotel_id=${hotelId}&start_date=${start}`;
        const res = await fetch(url, { headers: getAuthHeader() });
        
        if (!res.ok) {
            if (res.status === 403) { showNotification('Acceso denegado', 'error'); navigate('/dashboard'); return; }
            throw new Error('Error en la API');
        }
        
        const json = await res.json();
        if (json.success && json.data) {
          setMissingBatch(json.data.batch || []);
          setTotalMissing(json.data.total_missing || 0);
          
          const init = {};
          (json.data.batch || []).forEach((date) => { 
              init[date] = { occupied_rooms: '', average_rate: '' }; 
          });
          setInputData(init);
        } else {
          showNotification(json.error?.message || 'Respuesta inesperada', 'error');
        }
      } catch (err) {
        console.error(err);
        showNotification(err.message || 'Error buscando datos faltantes', 'error');
      } finally {
        setLoading(false);
      }
    }, [showNotification, navigate]);

  useEffect(() => {
    if (activeTab === 'missing' && selectedHotelId) {
      fetchMissingBatch(selectedHotelId, startDate);
    }
  }, [selectedHotelId, startDate, activeTab, fetchMissingBatch]);

  const handleInputChange = (date, field, value) => {
    setInputData((prev) => ({ ...prev, [date]: { ...prev[date], [field]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = missingBatch.map((date) => {
        const row = inputData[date] || {};
        if (!row.occupied_rooms || !row.average_rate) return null;
        return { 
            hotel_id: parseInt(selectedHotelId), 
            date, 
            occupied_rooms: parseInt(row.occupied_rooms), 
            average_rate: parseFloat(row.average_rate) 
        };
      }).filter(Boolean);

    if (payload.length === 0) {
      showNotification('Completa los campos', 'warning');
      setSaving(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api.php?path=bulk-save`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      
      if (json.success) {
        showNotification(`Guardados ${payload.length} registros.`, 'success');
        fetchMissingBatch(selectedHotelId, startDate);
      } else {
        showNotification(json.error?.message || 'Error al guardar', 'error');
      }
    } catch (err) {
      showNotification('Error de conexión al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  // --- TAB 2: SEARCH LOGIC ---
  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
        const params = new URLSearchParams(searchParams);
        const response = await fetch(`${API_URL}/api.php?path=retrospective/search&${params}`, { headers: getAuthHeader() });
        const data = await response.json();
        if (data.success) {
            setSearchResults(data.data);
        } else {
            showNotification(data.error?.message, 'error');
        }
    } catch (err) {
        showNotification(err.message, 'error');
    } finally {
        setLoading(false);
    }
  };

  // --- TAB 3: ANOMALIES (PRICE) ---
  const scanAnomalies = useCallback(async () => {
    setLoading(true);
    setEditingKey(null); 
    try {
        const response = await fetch(`${API_URL}/api.php?path=retrospective/anomalies`, { headers: getAuthHeader() });
        const data = await response.json();
        if (data.success) {
            setAnomalies(data.data);
        } else {
            showNotification(data.error?.message, 'error');
        }
    } catch (err) {
        showNotification(err.message, 'error');
    } finally {
        setLoading(false);
    }
  }, [showNotification]);

  // --- TAB 4: ANOMALIES (MAX CAPACITY) ---
  const scanMaxCapAnomalies = useCallback(async () => {
    setLoading(true);
    setEditingKey(null);
    try {
        const response = await fetch(`${API_URL}/api.php?path=retrospective/anomalies-max`, { headers: getAuthHeader() });
        const data = await response.json();
        if (data.success) {
            setMaxCapAnomalies(data.data);
        } else {
            showNotification(data.error?.message, 'error');
        }
    } catch (err) {
        showNotification(err.message, 'error');
    } finally {
        setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    if (activeTab === 'anomalies') scanAnomalies();
    if (activeTab === 'max_cap') scanMaxCapAnomalies();
  }, [activeTab, scanAnomalies, scanMaxCapAnomalies]);

  // --- EDITING LOGIC ---
  const startEditing = (row) => {
      const key = `${row.hotel_id}-${row.date}`;
      setEditingKey(key);
      setEditValues({
          occupied_rooms: row.occupied_rooms,
          average_rate: row.average_rate
      });
  };

  const cancelEditing = () => {
      setEditingKey(null);
      setEditValues({ occupied_rooms: '', average_rate: '' });
  };

  const saveCorrection = async (row) => {
      setLoading(true);
      try {
          const payload = {
              hotel_id: row.hotel_id,
              date: row.date,
              occupied_rooms: editValues.occupied_rooms,
              average_rate: editValues.average_rate
          };

          const response = await fetch(`${API_URL}/api.php?path=retrospective/update`, {
              method: 'POST',
              headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });
          const data = await response.json();

          if (data.success) {
              showNotification('Corrección guardada.', 'success');
              setEditingKey(null);
              if (activeTab === 'anomalies') scanAnomalies();
              if (activeTab === 'max_cap') scanMaxCapAnomalies();
          } else {
              showNotification(data.error?.message || 'Error al guardar', 'error');
          }
      } catch (err) {
          showNotification('Error de conexión', 'error');
      } finally {
          setLoading(false);
      }
  };

  // --- NEW: IGNORE ANOMALY LOGIC ---
  const handleIgnore = async (row) => {
    if (!window.confirm("¿Estás seguro de que este dato es correcto? Se ocultará de la lista.")) return;

    setLoading(true);
    try {
      const payload = {
        hotel_id: row.hotel_id,
        date: row.date
      };

      const response = await fetch(`${API_URL}/api.php?path=retrospective/ignore`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        showNotification('Registro marcado como correcto/ignorado.', 'success');
        // Refresh current tab
        if (activeTab === 'anomalies') scanAnomalies();
        if (activeTab === 'max_cap') scanMaxCapAnomalies();
      } else {
        showNotification(data.error?.message || 'Error al ignorar', 'error');
      }
    } catch (err) {
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="retrospective-container">
      <header className="retrospective-header">
        Ingreso Retrospectivo & Auditoría
        <small>Gestión de datos históricos y control de calidad (Solo Admin)</small>
      </header>

      {/* --- TABS NAVIGATION --- */}
      <div className="tabs-header">
        <button className={`tab-btn ${activeTab === 'missing' ? 'active' : ''}`} onClick={() => setActiveTab('missing')}>
            Datos Faltantes
        </button>
        <button className={`tab-btn ${activeTab === 'search' ? 'active' : ''}`} onClick={() => setActiveTab('search')}>
            Búsqueda por Fecha
        </button>
        <button className={`tab-btn ${activeTab === 'anomalies' ? 'active' : ''}`} onClick={() => setActiveTab('anomalies')}>
            Anomalías (Precios)
        </button>
        <button className={`tab-btn ${activeTab === 'max_cap' ? 'active' : ''}`} onClick={() => setActiveTab('max_cap')}>
            Anomalías (Capacidad)
        </button>
      </div>

      <div className="retrospective-card">
        
        {/* --- TAB 1: MISSING --- */}
        {activeTab === 'missing' && (
            <>
                <div className="retrospective-controls">
                    <div className="control-group">
                        <label>Hotel</label>
                        <select value={selectedHotelId} onChange={(e) => setSelectedHotelId(e.target.value)} className="form-select">
                            <option value="">-- Seleccionar hotel --</option>
                            {hotels.map((h) => (<option key={h.hotel_id} value={h.hotel_id}>{h.name}</option>))}
                        </select>
                    </div>
                    <div className="control-group">
                        <label>Buscar faltantes desde</label>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="form-input" />
                    </div>
                </div>

                {selectedHotelId && (
                    <div className={`status-box ${loading ? 'loading' : totalMissing > 0 ? 'warning' : 'success'}`}>
                        {loading ? <span>⏳ Escaneando...</span> : totalMissing > 0 ? 
                            <span>⚠️ <strong>{totalMissing}</strong> días faltantes.</span> : 
                            <span>✅ Base de datos completa desde fecha seleccionada.</span>}
                    </div>
                )}

                {!loading && missingBatch.length > 0 && (
                    <div className="table-wrapper">
                        <table className="retrospective-table">
                            <thead>
                                <tr><th>FECHA</th><th>OCUPADOS</th><th>TARIFA PROM.</th></tr>
                            </thead>
                            <tbody>
                                {missingBatch.map((date) => (
                                    <tr key={date}>
                                        <td className="date-cell">{format(new Date(date), 'EEEE, d MMMM yyyy', { locale: es })}</td>
                                        <td><input type="number" min="0" placeholder="0" value={inputData[date]?.occupied_rooms || ''} onChange={(e) => handleInputChange(date, 'occupied_rooms', e.target.value)} /></td>
                                        <td><input type="number" min="0" step="0.01" placeholder="0.00" value={inputData[date]?.average_rate || ''} onChange={(e) => handleInputChange(date, 'average_rate', e.target.value)} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="actions">
                            <button onClick={handleSave} disabled={saving} className="save-btn">{saving ? 'Guardando...' : 'GUARDAR Y CONTINUAR >>'}</button>
                        </div>
                    </div>
                )}
            </>
        )}

        {/* --- TAB 2: SEARCH --- */}
        {activeTab === 'search' && (
            <>
                <form className="retrospective-controls" onSubmit={handleSearch}>
                    <div className="control-group"><label>Desde</label><input type="date" value={searchParams.startDate} onChange={e => setSearchParams({...searchParams, startDate: e.target.value})} className="form-input"/></div>
                    <div className="control-group"><label>Hasta</label><input type="date" value={searchParams.endDate} onChange={e => setSearchParams({...searchParams, endDate: e.target.value})} className="form-input"/></div>
                    <div className="control-group"><label>Hotel (Opcional)</label>
                        <select value={searchParams.hotelId} onChange={e => setSearchParams({...searchParams, hotelId: e.target.value})} className="form-select">
                            <option value="">Todos</option>{hotels.map((h) => (<option key={h.hotel_id} value={h.hotel_id}>{h.name}</option>))}
                        </select>
                    </div>
                    <div className="control-group" style={{justifyContent: 'flex-end'}}><button type="submit" className="save-btn" style={{height: '42px', marginTop: 'auto'}}>Buscar</button></div>
                </form>

                {loading && <div className="status-box loading">Buscando...</div>}
                {!loading && searchResults.length > 0 ? (
                    <table className="retrospective-table">
                        <thead><tr><th>Hotel</th><th>Fecha</th><th>Hab. Vendidas</th><th>Tarifa Prom.</th><th>Ingreso Calc.</th></tr></thead>
                        <tbody>
                            {searchResults.map((row, i) => (
                                <tr key={i}><td>{row.hotel_name}</td><td>{row.date}</td><td>{row.occupied_rooms}</td><td>{renderCurrency(row.average_rate)}</td><td>{renderCurrency(row.calculated_revenue)}</td></tr>
                            ))}
                        </tbody>
                    </table>
                ) : (!loading && <div className="status-box">No se encontraron resultados para este rango.</div>)}
            </>
        )}

        {/* --- TAB 3: ANOMALIES (PRICE) --- */}
        {activeTab === 'anomalies' && (
            <>
                <div className="anomalies-header" style={{display:'flex', justifyContent:'space-between', marginBottom: '20px'}}>
                    <div>
                        <h3 style={{margin:0, color:'#2c3e50'}}>Posibles Errores de Captura (Precio)</h3>
                        <p style={{margin:0, color:'#7f8c8d', fontSize:'14px'}}>
                            Tarifas que desvían +/- 90% del promedio histórico del hotel.
                        </p>
                    </div>
                    <button className="save-btn" onClick={scanAnomalies}>Re-escanear</button>
                </div>

                {loading && <div className="status-box loading">Analizando base de datos...</div>}

                {!loading && anomalies.length === 0 ? (
                    <div className="status-box success">✅ No se encontraron anomalías de precio obvias.</div>
                ) : (!loading && (
                    <table className="retrospective-table">
                        <thead>
                            <tr>
                                <th>Hotel</th>
                                <th>Fecha</th>
                                <th>Hab.</th>
                                <th>Tarifa Capturada</th>
                                <th>Promedio Histórico</th>
                                <th>Desviación</th>
                                <th style={{textAlign: 'right'}}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {anomalies.map((row) => {
                                const rowKey = `${row.hotel_id}-${row.date}`;
                                const isEditing = editingKey === rowKey;

                                return (
                                    <tr key={rowKey} style={{backgroundColor: isEditing ? '#fef9e7' : 'inherit'}}>
                                        <td>{row.hotel_name}</td>
                                        <td>{row.date}</td>
                                        
                                        {/* Editable Fields */}
                                        {isEditing ? (
                                            <>
                                                <td>
                                                    <input 
                                                        type="number" 
                                                        value={editValues.occupied_rooms} 
                                                        onChange={e => setEditValues({...editValues, occupied_rooms: e.target.value})}
                                                        style={{width: '60px', padding: '4px'}}
                                                    />
                                                </td>
                                                <td>
                                                    <input 
                                                        type="number" 
                                                        step="0.01"
                                                        value={editValues.average_rate} 
                                                        onChange={e => setEditValues({...editValues, average_rate: e.target.value})}
                                                        style={{width: '100px', padding: '4px'}}
                                                    />
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td>{row.occupied_rooms}</td>
                                                <td style={{color:'#d32f2f', fontWeight:'bold'}}>{renderCurrency(row.average_rate)}</td>
                                            </>
                                        )}

                                        <td>{renderCurrency(row.expected_rate)}</td>
                                        
                                        <td style={{color: row.deviation_percent > 0 ? '#c62828' : '#ef6c00', fontWeight:'bold'}}>
                                            {row.deviation_percent > 0 ? '+' : ''}{row.deviation_percent}%
                                        </td>

                                        <td style={{textAlign: 'right'}}>
                                            {isEditing ? (
                                                <div style={{display: 'flex', gap: '5px', justifyContent: 'flex-end'}}>
                                                    <button onClick={() => saveCorrection(row)} className="action-btn" style={{backgroundColor: '#2e7d32', padding: '6px 12px', height: 'auto', fontSize:'12px'}}>Guardar</button>
                                                    <button onClick={cancelEditing} className="action-btn" style={{backgroundColor: '#757575', padding: '6px 12px', height: 'auto', fontSize:'12px'}}>X</button>
                                                </div>
                                            ) : (
                                                <div style={{display: 'flex', gap: '5px', justifyContent: 'flex-end'}}>
                                                    <button 
                                                        onClick={() => startEditing(row)} 
                                                        className="action-btn" 
                                                        style={{backgroundColor: '#f57c00', padding: '6px 12px', height: 'auto', fontSize:'12px'}}
                                                    >
                                                        Corregir
                                                    </button>
                                                    <button 
                                                        onClick={() => handleIgnore(row)} 
                                                        className="action-btn" 
                                                        style={{backgroundColor: '#78909c', padding: '6px 12px', height: 'auto', fontSize:'12px'}}
                                                    >
                                                        Ignorar
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ))}
            </>
        )}

        {/* --- TAB 4 CONTENT: ANOMALIES (MAX CAPACITY) --- */}
        {activeTab === 'max_cap' && (
            <>
                <div className="anomalies-header" style={{display:'flex', justifyContent:'space-between', marginBottom: '20px'}}>
                    <div>
                        <h3 style={{margin:0, color:'#2c3e50'}}>Errores de Capacidad</h3>
                        <p style={{margin:0, color:'#7f8c8d', fontSize:'14px'}}>
                            Días donde los cuartos ocupados superan el total de habitaciones del hotel.
                        </p>
                    </div>
                    <button className="save-btn" onClick={scanMaxCapAnomalies}>Re-escanear</button>
                </div>

                {loading && <div className="status-box loading">Analizando...</div>}

                {!loading && maxCapAnomalies.length === 0 ? (
                    <div className="status-box success">✅ Sin errores de capacidad.</div>
                ) : (!loading && (
                    <table className="retrospective-table">
                        <thead>
                            <tr>
                                <th>Hotel</th>
                                <th>Fecha</th>
                                <th>Ocupados (Err)</th>
                                <th>Capacidad Max</th>
                                <th>Exceso</th>
                                <th style={{textAlign: 'right'}}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {maxCapAnomalies.map((row) => {
                                const rowKey = `${row.hotel_id}-${row.date}`;
                                const isEditing = editingKey === rowKey;

                                return (
                                    <tr key={rowKey} style={{backgroundColor: isEditing ? '#fef9e7' : 'inherit'}}>
                                        <td>{row.hotel_name}</td>
                                        <td>{row.date}</td>
                                        
                                        {/* Editable Fields */}
                                        {isEditing ? (
                                            <>
                                                <td>
                                                    <input 
                                                        type="number" 
                                                        value={editValues.occupied_rooms} 
                                                        onChange={e => setEditValues({...editValues, occupied_rooms: e.target.value})}
                                                        style={{width: '60px', padding: '4px'}}
                                                    />
                                                </td>
                                                {/* Hidden rate input logic: keep existing rate */}
                                            </>
                                        ) : (
                                            <>
                                                <td style={{color:'#d32f2f', fontWeight:'bold'}}>{row.occupied_rooms}</td>
                                                <td>{row.max_capacity}</td>
                                            </>
                                        )}

                                        {!isEditing && (
                                            <td style={{color:'#c62828', fontWeight:'bold'}}>
                                                +{row.occupied_rooms - row.max_capacity}
                                            </td>
                                        )}
                                        {isEditing && <td>-</td>}

                                        <td style={{textAlign: 'right'}}>
                                            {isEditing ? (
                                                <div style={{display: 'flex', gap: '5px', justifyContent: 'flex-end'}}>
                                                    <button onClick={() => saveCorrection(row)} className="action-btn" style={{backgroundColor: '#2e7d32', padding: '6px 12px', height: 'auto', fontSize:'12px'}}>Guardar</button>
                                                    <button onClick={cancelEditing} className="action-btn" style={{backgroundColor: '#757575', padding: '6px 12px', height: 'auto', fontSize:'12px'}}>X</button>
                                                </div>
                                            ) : (
                                                <div style={{display: 'flex', gap: '5px', justifyContent: 'flex-end'}}>
                                                    <button 
                                                        onClick={() => startEditing(row)} 
                                                        className="action-btn" 
                                                        style={{backgroundColor: '#f57c00', padding: '6px 12px', height: 'auto', fontSize:'12px'}}
                                                    >
                                                        Corregir
                                                    </button>
                                                    <button 
                                                        onClick={() => handleIgnore(row)} 
                                                        className="action-btn" 
                                                        style={{backgroundColor: '#78909c', padding: '6px 12px', height: 'auto', fontSize:'12px'}}
                                                    >
                                                        Ignorar
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ))}
            </>
        )}

      </div>
    </div>
  );
}

export default RetrospectiveEntry;